import { StringHelpers } from "../../helpers/string-helpers";
import { IChartContainer } from "../../models/base/chart-container.interface";
import { ChartStyleSettingsModel } from "../../models/chart-style/chart-style-settings.model";
import { IndicatorType } from "../../models/enum/indicator-type.enum";
import { TiChartMenuContainer } from "../../ti-chart-menu.container";
import { IComponent } from "../component.interface";
import { EMACloudBuySellSummaryPopupComponent } from "../ema-cloud-buy-sell-summary-popup.component";
import { EMACloudSettingsPopupComponent } from "../ema-cloud-settings-popup";
import { IndicatorManagerPopupComponent } from "../indicator-manager-popup.component";
import { IndicatorLastPriceComponent } from "../indicators-last-price.component";
import { TiChartSeriesComponent } from "../ti-chart-series.component";

export class HighChartIndicatorsComponent implements IComponent {
    public id = '';

    private element: HTMLElement;
    private options: HTMLElement;
    private chartStyle: ChartStyleSettingsModel;
    private tiChartMenuContainer: TiChartMenuContainer;
    private base: IChartContainer;

    constructor(el: HTMLElement, tiChartMenuContainer: TiChartMenuContainer, base: IChartContainer) {
        this.id = StringHelpers.newGuid();
        this.element = el;
        this.options = this.element.findFirst('ul');
        this.tiChartMenuContainer = tiChartMenuContainer;
        this.base = base;
        this.subscribeChartService();
        this.onIndicatorIconClick();
    }

    public show() {
        this.element.addClass('active');
    }

    public hide() {
        this.element.removeClass('active');
        this.hideOptions();
    }

    private subscribeChartService() {
        this.base.chartService.subscribeChartStyleData((data: ChartStyleSettingsModel) => {
            this.chartStyle = data;
            this.toggleIndicators(data);
        });       
        this.base.chartService.subscribe_OpenIndicatorManager(() => {
            this.openIndicatorManager();
        });
    }

    private toggleIndicators(chartStyle: ChartStyleSettingsModel) {
        if (!chartStyle.highChartIndicatorsEnabled && !chartStyle.emaCloudEnabled) {
            this.element.addClass('hidden');
            return;
        }

        this.setTIPROMenuItems(chartStyle);

        this.element.removeClass('hidden');

        if (!chartStyle.highChartIndicatorsEnabled || !chartStyle.emaCloudEnabled) {
            this.options.querySelectorAll('li').forEach((el: HTMLElement) => {
                const value = parseInt(el.dataset.value);
                if ((value > 210 && !chartStyle.highChartIndicatorsEnabled) || (value == 28 && !chartStyle.emaCloudEnabled)) {
                    el.addClass('hidden');
                }
            });
        }
    }

    private setTIPROMenuItems(chartStyle: ChartStyleSettingsModel) {
        const tradeWaveMenuItem = this.options.findFirst('.tf[data-value="28"]');
        if (tradeWaveMenuItem && chartStyle.tradeWaveName) {
            tradeWaveMenuItem.findFirst('span')?.setInnerHtml(chartStyle.tradeWaveName);
        }

        if (!this.base.marketingWebChartMode) {
            const iconV1 = this.element.findFirst('svg.icon');
            const iconV2Light = this.element.findFirst('.light-img');
            const iconV2Dark = this.element.findFirst('.dark-img');

            const indItem1 = this.options.findFirst('.tf[data-value="216"]');
            const indItem2 = this.options.findFirst('.tf[data-value="217"]');
            const indItem3 = this.options.findFirst('.tf[data-value="999"]');
            const indItem4 = this.options.findFirst('.tf[data-value="9999"]');

            if (chartStyle && chartStyle.webBasedIndicatorManagerSupported && chartStyle.chartSettingsPopupModel?.displayNewIndicatorManager) {
                iconV1?.classList.add('hidden');
                iconV2Light?.classList.remove('hidden');
                iconV2Dark?.classList.remove('hidden');

                indItem1?.classList.add('hidden');
                indItem2?.classList.add('hidden');
                indItem3?.classList.remove('hidden');
                indItem4?.classList.remove('hidden');
            } else {
                iconV1?.classList.remove('hidden');
                iconV2Light?.classList.add('hidden');
                iconV2Dark?.classList.add('hidden');

                indItem1?.classList.remove('hidden');
                indItem2?.classList.remove('hidden');
                indItem3?.classList.add('hidden');
                indItem4?.classList.add('hidden');
            }
        }
    }

    private openIndicatorsMenu() {
        this.toggleIndicator(!this.tiChartMenuContainer.displayIndicator);

        if (this.options) {
            this.setElementPosition();

            if (this.tiChartMenuContainer.displayIndicator) {
                this.options.classList.add('active');
            } else {
                this.options.classList.remove('active');
            }
        }
    }

    private setElementPosition() {
        const parentRect = this.base.element.getBoundingClientRect();
        const rect = this.element.getBoundingClientRect();
        this.options.style.top =  ((rect.y - parentRect.y) + 20) + 'px';
        this.options.style.left = ((rect.x - parentRect.x)  - 4) + 'px';
    }

    private onIndicatorIconClick() {
        this.element.addEventListener('click', (ev: any) => {
            ev.stopPropagation();
            this.openIndicatorsMenu();
        });

        if (this.base.isTouchDevice && this.base.marketingWebChartMode) {
            this.element.addEventListener('mouseenter', (ev: any) => {
                ev.stopPropagation();
                this.openIndicatorsMenu();
            });
        }

        const allOptions = this.options.querySelectorAll<HTMLElement>('.tf');
        allOptions.forEach((op: HTMLElement) => {
            const value = parseInt(op.dataset.value, 10);
            if (value == IndicatorType.EMACloud) {
                this.tradeWaveOnClicks(op);
            } else if (value == 9999) {
                this.quickIndicatorOnClicks(op);
            } else {
                op.addEventListener('click', (ev: any) => {
                    ev.stopPropagation();
                    this.selectHighChartIndicator(op);
                });
            }
        });

        this.element.removeChild(this.options);
        this.base.chartWrapper.insertAdjacentElement('beforeend', this.options);
    }

    private quickIndicatorOnClicks(op: HTMLElement) {
        op.addEventListener('click', (e: any) => {
            e.preventDefault();
            e.stopPropagation();
            
            op.classList.toggle('sub-open');
        });

        let mouseEnterTimer: any = null;
        op.addEventListener('mouseenter', (e: any) => {
            e.preventDefault();
            e.stopPropagation();
            
            clearTimeout(mouseEnterTimer);
            op.classList.add('sub-open');
        });

        op.addEventListener('mouseleave', (e: any) => {
            e.preventDefault();
            e.stopPropagation();
            
            mouseEnterTimer = setTimeout(() => {
                op.classList.remove('sub-open');
            }, 600);
        });

        op.querySelectorAll<HTMLElement>('ul.sub li').forEach((el: HTMLElement) => {
            el.addEventListener('mouseenter', (e: any) => {
                if (!this.base.isTouchDevice || !this.base.marketingWebChartMode) {
                    return;
                }
                
                e.preventDefault();
                e.stopPropagation();

                this.addOrHideQuickIndicator(el);
            });

            el.addEventListener('click', (e: any) => {
                e.preventDefault();
                e.stopPropagation();

                this.addOrHideQuickIndicator(el);
            });
        });
    }

    private addOrHideQuickIndicator(el: HTMLElement) {
        const value = parseInt(el.dataset.value, 10);
        if (value == IndicatorType.IndicatorManager) {
            this.openIndicatorManager();
            return;
        }

        const popup = this.base.getComponentsByType(IndicatorManagerPopupComponent)?.[0] as IndicatorManagerPopupComponent;
        if (!el.classList.contains('active')) {
            el.classList.add('active');
            const valueOneStr = el.getAttribute('data-value-one');
            const valueOne = valueOneStr ? parseInt(valueOneStr) : undefined;
            popup?.quickAdd(value, valueOne);
        } else {
            popup?.quickRemove(value);
            el.classList.remove('active');
        }

        this.tiChartMenuContainer.closeAllMenuControls();
    }

    private tradeWaveOnClicks(op: HTMLElement) {
        op.addEventListener('mouseenter', (e: any) => {
            if (!this.base.isTouchDevice || !this.base.marketingWebChartMode) {
                return;
            }

            this.toggleIndicator(false);
            
            if (!op.classList.contains('active')) { 
                this.addTradeWave();
               op.classList.add('active');
           } else {
               this.hideTradeWave();
               op.classList.remove('active');
           }
        });

        const editSvg = op.querySelector('.ico-edit');
        if (editSvg) {
            editSvg.addEventListener('click', (e: any) => {
                e.preventDefault();
                e.stopPropagation();
                this.toggleIndicator(false);

                if (!this.base.marketingWebChartMode && (!this.chartStyle?.webBasedIndicatorManagerSupported || !this.chartStyle?.chartSettingsPopupModel?.displayNewIndicatorManager)) {
                    const popup = this.base.getComponentsByType(EMACloudSettingsPopupComponent)?.[0] as EMACloudSettingsPopupComponent;
                    popup?.show();
                } else {
                    // ema cloud settings for website
                    const popup = this.base.getComponentsByType(IndicatorManagerPopupComponent)?.[0] as IndicatorManagerPopupComponent;
                    popup?.showEMACloud();
                }
            });
        }

        const tradeWaveSvg = op.querySelector('.ico-trade-wave');
        if (tradeWaveSvg) {
            tradeWaveSvg.addEventListener('click', (e: any) => {
                e.preventDefault();
                e.stopPropagation();
                this.toggleIndicator(false);

                if (!op.classList.contains('active')) { 
                     this.addTradeWave();
                    op.classList.add('active');
                } else {
                    this.hideTradeWave();
                    op.classList.remove('active');
                }
            });
        }

        const span = op.querySelector('span');
        if (span) {
            span.addEventListener('click', (e: any) => {
                e.preventDefault();
                e.stopPropagation();
                this.toggleIndicator(false);

                if (!op.classList.contains('active')) { 
                     this.addTradeWave();
                    op.classList.add('active');
                } else {
                    this.hideTradeWave();
                    op.classList.remove('active');
                }
            });
        }

        const aTag = op.querySelector('a');
        if (aTag) {
            aTag.addEventListener('click', (e: any) => {
                e.preventDefault();
                e.stopPropagation();
                this.toggleIndicator(false);

                const popup = this.base.getComponentsByType(EMACloudBuySellSummaryPopupComponent)?.[0] as EMACloudBuySellSummaryPopupComponent;
                if (popup) {
                    if (popup.isActive()) {
                        popup.close();
                    } else {
                        popup.show();
                    }
                }
            });
        }
    }

    private addTradeWave() {
        if (this.base.marketingWebChartMode) {
            const popup = this.base.getComponentsByType(IndicatorManagerPopupComponent)?.[0] as IndicatorManagerPopupComponent;
            popup?.syncEMACloud(true);
        } else {
            const settings = this.base.getComponentsByType(EMACloudSettingsPopupComponent)?.[0] as EMACloudSettingsPopupComponent;
            settings?.render(true);
        }
    }

    private hideTradeWave() {
        this.base.chartService.requestSaveEMACloudSettings(null);
        const popup = this.base.getComponentsByType(EMACloudBuySellSummaryPopupComponent)?.[0] as EMACloudBuySellSummaryPopupComponent;
        popup?.close(true);
    }

    private selectHighChartIndicator(op: HTMLElement) {
        if (!op) { return; }
        const value = parseInt(op.dataset.value, 10);
        if (value === 9999) return;
        
        this.base.drawingToolsManager?.clearAll();

        const mainChartSeries = this.base.getComponentsByType(TiChartSeriesComponent)[0] as TiChartSeriesComponent;
        if (!op.classList.contains('active')) {
            if (value == IndicatorType.EMACloud) {
                // ema cloud settings for website
                this.addTradeWave();

                op.classList.add('active');
                this.tiChartMenuContainer.closeAllMenuControls();

                return;
            } else if (value == IndicatorType.IndicatorManager) {
                this.openIndicatorManager();

                return;
            }

            op.classList.add('active');
            mainChartSeries?.addHighChartIndicator(value);
        } else {
            if (value == IndicatorType.EMACloud) {
                this.hideTradeWave();
            }

            op.classList.remove('active');
            mainChartSeries?.removeHighChartIndicator(value);
        }

        let indicatorLastPriceComponent = this.base.getComponentsByType(IndicatorLastPriceComponent)[0] as IndicatorLastPriceComponent
        indicatorLastPriceComponent.createLastPriceForIndicators();

        this.tiChartMenuContainer.closeAllMenuControls();

        setTimeout(() => {
            this.base.drawingToolsManager?.sync(0);
        }, 300);
    }

    private openIndicatorManager() {
        const popup = this.base.getComponentsByType(IndicatorManagerPopupComponent)?.[0] as IndicatorManagerPopupComponent;
        popup?.show();
        this.toggleIndicator(false);
    }

    public toggleIndicator(active: boolean) {
        this.tiChartMenuContainer.displayChartType = false;
        this.tiChartMenuContainer.displayTimeframe = false;
        this.tiChartMenuContainer.displayIndicator = active;
        this.tiChartMenuContainer.displayTools = false;
        this.tiChartMenuContainer.displayTradeOrderType = false;
        this.tiChartMenuContainer.toggleControls();
    }

    private hideOptions() {
        this.element.removeClass('active');
        this.options?.removeClass('active');
        this.options?.querySelectorAll<HTMLElement>('.sub-open')?.forEach(x => {
            x.classList.remove('sub-open');
        });

        this.tiChartMenuContainer.displayIndicator = false;
    }
}
